import { focusManager, QueryCache, QueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { handleError } from '@/utils/errorUtils';

/**
 * Highest HTTP status that counts as a client mistake. Statuses 400-499 are "the request is
 * wrong"; asking again returns the same answer.
 */
const CLIENT_ERROR_MIN = 400;
const CLIENT_ERROR_MAX = 499;

/** 4xx answers that can change on a second try: request timeout and "too many requests". */
const RETRYABLE_CLIENT_STATUSES = new Set([408, 429]);

/** Retries after the first failed call. One retry in total (a call is made at most twice). */
const MAX_RETRIES = 1;

/**
 * Retry rule for every query.
 *
 * What: decides whether React Query makes one more call after a failed call.
 * Rules, in order:
 * 1. Cancelled call (`axios.isCancel`): never. The page left on purpose, so there is nothing to
 *    retry.
 * 2. Not an axios error: never. That is a bug in our own code (for example a `TypeError` while
 *    mapping the answer). A second try gives the same bug. It is logged once through
 *    `QueryCache.onError` below.
 * 3. 4xx answer other than 408/429: never. The server said the request is wrong, so the answer
 *    does not change.
 * 4. Everything else (5xx, 408, 429, network error with no answer): once (`failureCount < 1`).
 *
 * Why 4xx is never retried: a 401 is already refreshed and retried once by the shared
 * `refreshTokenInterceptor` (`@altirllc/shared`) before the error reaches React Query. If React
 * Query retried it too, the two retries would stack and one expired token would cause up to four
 * calls.
 *
 * Constraint: this function must not log. `QueryCache.onError` logs every final error exactly
 * once; logging here would log once per attempt.
 *
 * @param failureCount Number of failed calls so far (0 after the first failure).
 * @param error Whatever the query function threw.
 */
export const shouldRetryQuery = (failureCount: number, error: unknown): boolean => {
  if (axios.isCancel(error)) {
    return false;
  }

  if (!axios.isAxiosError(error)) {
    return false;
  }

  const status = error.response?.status;

  if (
    status !== undefined &&
    status >= CLIENT_ERROR_MIN &&
    status <= CLIENT_ERROR_MAX &&
    !RETRYABLE_CLIENT_STATUSES.has(status)
  ) {
    return false;
  }

  return failureCount < MAX_RETRIES;
};

/**
 * The one `QueryClient` of the portal (module singleton, mounted in `src/index.tsx`).
 *
 * What it sets:
 * - `staleTime` 30 s (owner decision, 2026-10-22; was 10 s, before that 60 s): data counts as fresh
 *   for 30 seconds.
 *   Opening a page again inside that window shows the saved data and makes no call. After it, the
 *   page shows the saved data at once and refreshes it quietly in the background. This is the
 *   only place the window is set; query factories say "app default" and never repeat the number.
 * - `cacheTime` stays at the v4 default (5 min): data no page uses is removed after 5 minutes.
 * - `refetchOnWindowFocus` and `refetchOnReconnect` stay at the v4 default (`true`): stale data
 *   refreshes when the operator comes back to the browser tab or the network comes back. "Comes
 *   back to the tab" is narrowed by `listenToTabVisibilityOnly` below.
 * - `retry`: see `shouldRetryQuery`.
 * Per-query overrides: lookups set `staleTime: Infinity` (see `src/queries/lookup.ts`).
 *
 * Logging: `QueryCache.onError` logs every final query error once with category
 * `UNHANDLED_RESPONSE`. `handleError` already skips cancelled calls. Pages therefore must not log
 * query errors themselves.
 * There is no `MutationCache.onError` on purpose: every save caller already shows its own message and
 * calls `handleError`, so a global log would log twice.
 *
 * Sign-out: `logout()` in `src/utils/authUtils.ts` calls `queryClient.clear()`, so the next
 * partner never sees the previous partner's data.
 */
/**
 * Focus refresh: only when the browser tab becomes visible again (owner decision, 2026-10-13).
 *
 * What: replaces React Query's focus listener. v4 listens to `visibilitychange` AND window `focus`;
 * this keeps only `visibilitychange`.
 * Why: window `focus` fires on every click back into the page from another window (another app,
 * alt-tab, the browser DevTools). With a short fresh window that refreshed every stale query on
 * screen several times a minute. Coming back from another browser tab (or un-minimising the
 * browser) still fires `visibilitychange`, so "the operator came back to the portal" still
 * refreshes stale data.
 * Constraint: module scope, before any query subscribes, so the listener is in place from the
 * first render. `handleFocus()` with no argument lets React Query read `document.visibilityState`
 * itself, so becoming hidden does not refetch.
 */
const listenToTabVisibilityOnly = (handleFocus: (focused?: boolean) => void) => {
  if (typeof window === 'undefined' || !window.addEventListener) {
    return undefined;
  }

  const listener = () => handleFocus();

  window.addEventListener('visibilitychange', listener, false);

  return () => {
    window.removeEventListener('visibilitychange', listener);
  };
};

focusManager.setEventListener(listenToTabVisibilityOnly);

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: shouldRetryQuery,
    },
  },
  queryCache: new QueryCache({
    onError: (error) => {
      handleError(error, 'UNHANDLED_RESPONSE');
    },
  }),
});
